import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  useMediaQuery,
  IconButton,
  Typography,
  Divider,
  Link,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import MateriomLogo from "../ui/MateriomLogo";
import { UserDropdownMenu } from "../TopBar";
import { useTheme } from "@mui/material/styles";
import { createTopBarStyles, createTryMateriomAIButtonStyles } from "./styles";
import { ROUTES } from "../../constants/routes";
import { GlitterIcon } from "../ui/GlitterIcon";
import { useAuth } from "../../hooks/useAuth";
import { User } from "../../types/api";
import { FOOTER_URLS } from "../../constants";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { CircularProgress } from "@mui/material";
import { FeatureRequestBanner } from "../FeatureRequestBanner";
import { AgentTrialModal } from "../AgentTrialModal";
import { useAuthContext } from "../../contexts/AuthContext";
import { useIsEnterprise } from "../../hooks/useIsEnterprise";
import AccessDenied from "../../pages/AccessDenied";

/**
 * Feature flag name for the Materiom AI v2 feature flag
 *
 * TODO: Might be better to move this to a central location, like a constants file.
 * This way we can use the same feature flag name in multiple places.
 * For example, in the AgentTrialModal component and the AuthenticatedMateriomAI component.
 * @constant
 */
const MATERIOM_AI_V2_FEATURE_FLAG_NAME = "materiom-ai-v2-feature-flag";

/**
 * Floating button to promote Materiom AI v2 agent mode
 * Expands on hover to show full description
 */
const TryMateriomAIButton: React.FC<{
  isVisible: boolean;
  onNavigate: () => void;
}> = ({ isVisible, onNavigate }) => {
  const [isHovered, setIsHovered] = useState(false);
  const styles = createTryMateriomAIButtonStyles(isHovered);

  if (!isVisible) return null;

  return (
    <Button
      variant="contained"
      onClick={onNavigate}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disableRipple
      sx={styles.button}
      startIcon={<GlitterIcon sx={styles.icon} />}
    >
      <Box component="span" sx={styles.text}>
        {isHovered
          ? "Try the latest agentic AI tool for bio-based materials discovery"
          : "Try Materiom AI v2"}
      </Box>
    </Button>
  );
};

interface AuthenticatedProps {
  children: React.ReactNode;
}

/**
 * Authenticated Layout Component
 *
 * A responsive navigation bar with MateriomLogo and navigation buttons.
 * Shows full navigation on desktop and full-screen mobile menu on mobile.
 * Navigation paths are managed centrally in constants/routes.ts.
 * Includes a footer with Materiom copyright notice.
 */
const Authenticated: React.FC<AuthenticatedProps> = ({ children }) => {
  // Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const [showingNavigationDropdown, setShowingNavigationDropdown] =
    useState(false);

  // Authentication hooks
  const { user } = useAuthContext();
  const { logout } = useAuth();

  // Feature flag controlling access to agent mode
  const featureFlagValue = useFeatureFlagEnabled(
    MATERIOM_AI_V2_FEATURE_FLAG_NAME
  );

  // Enterprise access: hostname check + PostHog cohort flag
  const { isEnterprise, isEnterpriseDomain, isAccessDenied, isFlagLoading } =
    useIsEnterprise();

  // Enterprise users always have agent mode accessible
  const isAgentModeAccessible = featureFlagValue === true || isEnterprise;

  // Styles
  const theme = useTheme();
  const topBarStyles = createTopBarStyles(theme);

  // Use Material-UI breakpoints to determine if we're on mobile
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isActive = (path: string) => location.pathname === path;

  // Navigation items using centralized route constants
  // Enterprise users don't see User Directory and Contribution Portal
  const navItems = [
    { label: "Dashboard", path: ROUTES.DASHBOARD },
    { label: "Materiom AI", path: ROUTES.MATERIOM_AI },
    { label: "Materials Database", path: ROUTES.MATERIALS_DATABASE },
    ...(!isEnterprise
      ? [
          { label: "User Directory", path: ROUTES.USER_DIRECTORY },
          { label: "Contribution Portal", path: ROUTES.CONTRIBUTION_PORTAL },
        ]
      : []),
  ];

  const handleMobileMenuToggle = () => {
    setShowingNavigationDropdown(!showingNavigationDropdown);
  };

  const handleMobileNavigation = (path: string) => {
    navigate(path);
    setShowingNavigationDropdown(false);
  };

  const handleLogout = () => {
    logout();
    setShowingNavigationDropdown(false);
  };

  // Get user name
  const getUserName = (user: User | null) => {
    if (user) {
      return user.name || "";
    }
    return "";
  };

  // Enterprise domain guard: show spinner while flag loads
  if (isEnterpriseDomain && isFlagLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: "#F5F5F5",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Enterprise domain guard: show access denied if user lacks enterprise access
  if (isAccessDenied) {
    return <AccessDenied />;
  }

  return (
    <Box
      sx={{
        ...topBarStyles.container,
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <AppBar position="fixed" elevation={1} sx={topBarStyles.appBar}>
        <Container maxWidth="xl">
          <Toolbar sx={topBarStyles.toolbar}>
            {/* Logo */}
            <Box sx={topBarStyles.logoContainer}>
              <MateriomLogo
                sx={topBarStyles.logo}
                onClick={() => navigate(ROUTES.DASHBOARD)}
              />
            </Box>

            {/* Desktop Navigation - Hidden on mobile */}
            {!isMobile && (
              <Box sx={topBarStyles.desktopNavigation}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    sx={topBarStyles.navButton(isActive(item.path))}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Mobile/Desktop Navigation */}
            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="toggle mobile menu"
                edge="end"
                onClick={handleMobileMenuToggle}
                disableRipple
                sx={topBarStyles.mobileMenuButton}
              >
                {showingNavigationDropdown ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            ) : (
              <UserDropdownMenu handleLogout={handleLogout} user={user} />
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Full-Screen Menu */}
      {isMobile && showingNavigationDropdown && (
        <Box sx={topBarStyles.mobileMenuOverlay}>
          {/* Main Navigation Links */}
          <Box sx={topBarStyles.mobileNavSection}>
            {navItems.map((item) => (
              <Box
                key={item.path}
                onClick={() => handleMobileNavigation(item.path)}
                sx={topBarStyles.mobileNavItem(isActive(item.path))}
              >
                <Typography
                  sx={topBarStyles.mobileNavItemText(isActive(item.path))}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Divider />

          {/* User Information Section */}
          <Box sx={topBarStyles.mobileUserSection}>
            <Box sx={topBarStyles.mobileUserInfo}>
              <Typography sx={topBarStyles.mobileUserName}>
                {getUserName(user)}
              </Typography>
            </Box>

            <Divider sx={{ mt: 2 }} />

            {/* Update Details Button */}
            <Box
              onClick={() => handleMobileNavigation(ROUTES.UPDATE_DETAILS)}
              sx={topBarStyles.mobileNavItem(isActive(ROUTES.UPDATE_DETAILS))}
            >
              <Typography
                sx={topBarStyles.mobileNavItemText(
                  isActive(ROUTES.UPDATE_DETAILS)
                )}
              >
                Update Details
              </Typography>
            </Box>

            {/* Logout Button */}
            <Box onClick={handleLogout} sx={topBarStyles.mobileLogoutButton}>
              <Typography
                sx={topBarStyles.mobileLogoutText}
                onClick={handleLogout}
              >
                Log Out
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ ...topBarStyles.content, flex: 1 }}>{children}</Box>

      {/* Feature Request Banner */}
      {!isEnterprise && <FeatureRequestBanner user={user} />}

      {/* Materiom AI v2 conditional components */}
      {/* Enterprise users are always in agentic mode, so skip trial modal and promo button */}
      {isAgentModeAccessible && !isEnterprise && (
        <>
          {/* Agent Trial Modal - only shown when feature flag is enabled */}
          <AgentTrialModal user={user} />
          {/* Floating "Try Materiom AI v2" Button */}
          <TryMateriomAIButton
            isVisible={!location.pathname.startsWith(ROUTES.MATERIOM_AI)}
            onNavigate={() => navigate(`${ROUTES.MATERIOM_AI}?mode=agent`)}
          />
        </>
      )}

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 5,
          backgroundColor: "white",
          mt: 6,
          borderTop: "2px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ fontSize: "0.875rem" }}
          >
            © {new Date().getFullYear()} Materiom |{" "}
            <Link
              href={FOOTER_URLS.TERMS_AND_CONDITIONS}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              underline="hover"
            >
              Terms & Conditions
            </Link>{" "}
            |{" "}
            <Link
              href={FOOTER_URLS.PRIVACY_POLICY}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              underline="hover"
            >
              Privacy Policy
            </Link>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Authenticated;
